// Confirmar un presupuesto tiene que producir EXACTAMENTE los mismos efectos
// que una venta real del POS (ver VentasPOS.jsx: applyStockOperationsForSale
// y la bifurcación post-venta ~línea 3100). Esta util centraliza esa lógica
// para que Presupuestos.jsx no la duplique (y la implemente mal, que es como
// llegamos al bug original: stock descontado sin kardex, sin turno de caja,
// balance de cliente tocado a mano incluso de contado, sin idempotencia).
//
// Nota de diseño: `supabase` y las funciones de cuenta corriente se cargan con
// `import()` dinámico y solo si el caller no las inyectó. `./salesReceivables.js`
// importa `../supabase`, que lee `import.meta.env` — algo que solo existe bajo
// Vite y que revienta al cargar el módulo bajo `node --test`. Las reglas puras
// de condición de pago, en cambio, viven en `./paymentTerms.js` justamente para
// poder importarse acá de forma estática, sin copiarlas.
import { addSettlementDays } from './paymentSettlement.js';
import { buildSalePaymentTerms, shouldUseClientAccountForSale } from './paymentTerms.js';
import { applyStockDelta } from './variantStock.js';

// --- carga perezosa de dependencias con efectos reales (supabase, deps) --- //

async function loadDefaultSupabaseClient() {
  const mod = await import('../supabase.js');
  return mod.supabase;
}

async function loadDefaultDeps() {
  const mod = await import('./salesReceivables.js');
  return {
    syncSaleClientDebit: mod.syncSaleClientDebit,
    syncSaleReceivable: mod.syncSaleReceivable,
  };
}

// --- helpers de stock / kardex (réplica de VentasPOS.applyStockOperationsForSale) --- //

async function descontarStockYRegistrarKardex({
  supabaseClient,
  organizationId,
  siteId,
  saleItems,
  saleShortId,
  nowIso,
}) {
  const allItems = (Array.isArray(saleItems) ? saleItems : []).filter((item) => item?.product_id);
  if (allItems.length === 0) return;

  // Talles y colores: las líneas de productos con stock por talle descuentan de
  // su variante vía fn_mover_stock (que además deja el kardex con variant_id).
  // Se valida TODO antes de tocar stock para no dejar la confirmación a medias.
  const productIds = [...new Set(allItems.map((item) => item.product_id))];
  const { data: trackingRows, error: trackingError } = await supabaseClient
    .from('products')
    .select('id, stock_tracking')
    .in('id', productIds);
  if (trackingError) throw trackingError;
  const variantTrackedIds = new Set(
    (Array.isArray(trackingRows) ? trackingRows : [])
      .filter((row) => row?.stock_tracking === 'variant')
      .map((row) => row.id),
  );
  const variantItems = allItems.filter((item) => variantTrackedIds.has(item.product_id));
  const sinTalle = variantItems.find((item) => !item.variant_id);
  if (sinTalle) {
    throw new Error(`"${sinTalle.product_name_snapshot || 'Producto'}" se vende por talle/color y el presupuesto no indica cuál. Rehacelo desde el POS eligiendo el talle.`);
  }
  for (const item of variantItems) {
    await applyStockDelta({
      supabase: supabaseClient,
      organizationId,
      productId: item.product_id,
      variantId: item.variant_id,
      siteId,
      delta: -(Number(item.quantity) || 0),
      tipo: 'Egreso',
      motivo: `Venta POS #${saleShortId} (Presupuesto confirmado).`,
      allowNegative: true,
    });
  }

  const items = allItems.filter((item) => !variantTrackedIds.has(item.product_id));
  if (items.length === 0) return;

  // Igual que en el POS: la cantidad a descontar de stock_levels se agrega
  // por producto (por si el mismo producto aparece en más de un ítem), pero
  // el kardex deja un movimiento por ítem original.
  const quantitiesByProduct = new Map();
  for (const item of items) {
    const quantity = Number(item.quantity) || 0;
    quantitiesByProduct.set(
      item.product_id,
      (quantitiesByProduct.get(item.product_id) || 0) + quantity,
    );
  }

  for (const [productId, quantity] of quantitiesByProduct) {
    const { data: stockRow, error: stockReadError } = await supabaseClient
      .from('stock_levels')
      .select('quantity')
      .eq('product_id', productId)
      .eq('site_id', siteId)
      .maybeSingle();

    if (stockReadError && stockReadError.code !== 'PGRST116') throw stockReadError;

    const currentQty = Number(stockRow?.quantity) || 0;

    const { error: upsertError } = await supabaseClient.from('stock_levels').upsert(
      {
        organization_id: organizationId,
        product_id: productId,
        site_id: siteId,
        quantity: currentQty - quantity,
        updated_at: nowIso,
      },
      { onConflict: 'product_id,site_id' },
    );

    if (upsertError) throw upsertError;
  }

  const movementRows = items.map((item) => ({
    organization_id: organizationId,
    producto_id: item.product_id,
    tipo: 'Egreso',
    cantidad: Number(item.quantity) || 0,
    // El prefijo "Venta POS #" + los 8 primeros hex del id en mayúsculas es
    // lo que usan los reportes/cruces existentes para vincular el kardex a
    // la venta. No tocar ese formato.
    motivo: `Venta POS #${saleShortId} (Presupuesto confirmado).`,
    sitio_origen: siteId,
    sitio_destino: null,
  }));

  const { error: movementError } = await supabaseClient.from('movimientos_stock').insert(movementRows);
  if (movementError) throw movementError;
}

export async function confirmarPresupuesto({
  presupuesto,
  organizationId,
  userId,
  cliente,
  organizacion,
  metodoPago,
  turnoCajaId,
  ahoraIso,
  supabaseClient,
  deps,
}) {
  // Idempotencia: si el presupuesto ya no está pendiente de confirmar, no
  // repetir ningún efecto (stock, caja, cuenta corriente).
  if (presupuesto?.status !== 'pending' || presupuesto?.invoice_status !== 'presupuesto') {
    return { ok: false, skipped: 'ya_confirmado' };
  }

  const client = supabaseClient || (await loadDefaultSupabaseClient());
  const { syncSaleClientDebit, syncSaleReceivable } = deps || (await loadDefaultDeps());

  const nowIso = ahoraIso || new Date().toISOString();
  const saleId = presupuesto.id;
  const saleShortId = String(saleId).slice(0, 8).toUpperCase();
  const clienteId = presupuesto.client_id || cliente?.id || null;
  const totalFinal = Number(presupuesto.total) || 0;
  const siteId = presupuesto.site_id;

  await descontarStockYRegistrarKardex({
    supabaseClient: client,
    organizationId,
    siteId,
    saleItems: presupuesto.sale_items,
    saleShortId,
    nowIso,
  });

  const salePaymentTerms = buildSalePaymentTerms({
    createdAt: nowIso,
    sale: presupuesto,
    client: cliente,
  });

  const shouldDeferCollection = shouldUseClientAccountForSale({
    organization: organizacion,
    client: cliente,
    paymentConditionDays: salePaymentTerms.days,
  });

  if (shouldDeferCollection && clienteId) {
    // Cuenta corriente: se genera la deuda y la cuenta por cobrar. Nunca se
    // toca `clients.balance` a mano ni se impacta caja/banco: todavía no se
    // cobró nada.
    await syncSaleClientDebit({
      saleId,
      organizationId,
      clientId: clienteId,
      totalAmount: totalFinal,
      movementType: 'Venta POS',
      observations: `Venta POS pendiente de cobro (presupuesto confirmado). Cliente: ${cliente?.full_name || 'Cliente'}.`,
      shouldApply: true,
    });

    await syncSaleReceivable({
      saleId,
      organizationId,
      clientId: clienteId,
      siteId,
      source: 'pos',
      description: `Venta POS #${saleShortId} - ${cliente?.full_name || 'Cliente'} (${salePaymentTerms.label})`,
      dueDate: salePaymentTerms.dueDate,
      totalAmount: totalFinal,
      active: true,
    });
  } else {
    const commissionPercentage = Math.max(0, Number(metodoPago?.commission_percentage) || 0);
    const accreditationDays = Math.max(0, Number(metodoPago?.accreditation_days) || 0);
    const businessDaysOnly = Boolean(metodoPago?.accreditation_business_days);
    const commissionAmount = totalFinal * (commissionPercentage / 100);
    const bankNetAmount = Math.max(0, totalFinal - commissionAmount);
    const settlementDate = addSettlementDays(nowIso, accreditationDays, businessDaysOnly);
    const settlesImmediately = Boolean(metodoPago?.bank_id) && accreditationDays === 0;

    let saldoResultante = null;
    if (settlesImmediately) {
      const { data: banco, error: bancoError } = await client
        .from('banks')
        .select('id, current_balance')
        .eq('id', metodoPago.bank_id)
        .single();

      if (bancoError) throw bancoError;

      saldoResultante = (Number(banco?.current_balance) || 0) + bankNetAmount;

      const { error: bankUpdateError } = await client
        .from('banks')
        .update({ current_balance: saldoResultante })
        .eq('id', metodoPago.bank_id);

      if (bankUpdateError) throw bankUpdateError;
    }

    const descripcion = `Venta POS #${saleShortId} (Presupuesto confirmado). Cliente: ${cliente?.full_name || 'Consumidor Final'}.${metodoPago?.bank_id
      ? ` Bruto: ${totalFinal}. Comision ${commissionPercentage}%: ${commissionAmount}. Neto acreditado: ${bankNetAmount}. Acreditacion: ${accreditationDays} dia(s)${businessDaysOnly ? ' habiles' : ''}.`
      : ''
      }`;

    const { error: mfError } = await client.from('movimientos_financieros').insert([
      {
        organization_id: organizationId,
        bank_id: metodoPago?.bank_id || null,
        sitio_id: siteId,
        tipo: 'Ingreso',
        categoria: 'Venta',
        monto: metodoPago?.bank_id ? bankNetAmount : totalFinal,
        saldo_resultante: saldoResultante,
        entidad_tipo: 'Cliente',
        entidad_id: clienteId,
        documento_tipo: 'Venta',
        documento_id: saleId,
        descripcion,
        fecha_operacion: metodoPago?.bank_id ? settlementDate : nowIso,
        user_id: userId,
      },
    ]);

    if (mfError) throw mfError;

    if (turnoCajaId) {
      const { error: cajaError } = await client.from('movimientos_caja').insert({
        turno_id: turnoCajaId,
        tipo: 'Ingreso',
        categoria: 'Venta',
        monto: totalFinal,
        descripcion: `Venta POS #${saleShortId} (Presupuesto confirmado - ${metodoPago?.name || 'Pago'}).`,
        organization_id: organizationId,
        payment_method_id: metodoPago?.id || null,
        fecha: nowIso,
      });

      if (cajaError) throw cajaError;
    }
  }

  const { error: updateError } = await client
    .from('sales')
    .update({
      status: 'completed',
      invoice_status: 'pendiente',
      created_at: nowIso,
    })
    .eq('id', saleId);

  if (updateError) throw updateError;

  return { ok: true, saleId };
}
